import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { Absence } from "./models/Absence";
import {
  createAbsence,
  deleteAbsence,
  fetchAbsences,
  fetchAbsencesForDay,
  fetchAbsencesRange,
  updateAbsence,
} from "./slice-extensions/absences-slice-extension";

interface AbsencesState {
  /** The paginated list page. */
  items: Absence[];
  totalCount: number;
  page: number;
  /** Flat range read backing the availability month grid. */
  rangeItems: Absence[];
  /** Per-day reads backing the attendance "away" badges, keyed by yyyy-MM-dd. */
  dayItems: Record<string, Absence[]>;
  status: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: AbsencesState = {
  items: [],
  totalCount: 0,
  page: 1,
  rangeItems: [],
  dayItems: {},
  status: LoadingStatus.Idle,
  lastError: undefined,
};

const upsert = (list: Absence[], absence: Absence): Absence[] => {
  const index = list.findIndex((item) => item.id === absence.id);
  if (index >= 0) {
    return list.map((item) => (item.id === absence.id ? absence : item));
  }
  return [...list, absence];
};

const absencesSlice = createSlice({
  name: "absences",
  initialState,
  reducers: {
    clearAbsencesError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAbsences.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchAbsences.fulfilled, (state, action) => {
        state.status = LoadingStatus.Idle;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.page = action.payload.page;
      })
      .addCase(fetchAbsences.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    builder
      .addCase(fetchAbsencesRange.fulfilled, (state, action: PayloadAction<Absence[]>) => {
        state.rangeItems = action.payload;
      })
      .addCase(fetchAbsencesRange.rejected, (state, action) => {
        state.lastError = action.payload;
      });

    builder.addCase(fetchAbsencesForDay.fulfilled, (state, action) => {
      state.dayItems[action.payload.date] = action.payload.items;
    });

    builder
      .addCase(createAbsence.fulfilled, (state, action: PayloadAction<Absence>) => {
        state.items = upsert(state.items, action.payload);
        state.rangeItems = upsert(state.rangeItems, action.payload);
        state.totalCount += 1;
      })
      .addCase(createAbsence.rejected, (state, action) => {
        state.lastError = action.payload;
      });

    builder
      .addCase(updateAbsence.fulfilled, (state, action: PayloadAction<Absence>) => {
        state.items = upsert(state.items, action.payload);
        state.rangeItems = upsert(state.rangeItems, action.payload);
      })
      .addCase(updateAbsence.rejected, (state, action) => {
        state.lastError = action.payload;
      });

    builder
      .addCase(deleteAbsence.fulfilled, (state, action: PayloadAction<string>) => {
        state.items = state.items.filter((absence) => absence.id !== action.payload);
        state.rangeItems = state.rangeItems.filter((absence) => absence.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteAbsence.rejected, (state, action) => {
        state.lastError = action.payload;
      });
  },
});

export const { clearAbsencesError } = absencesSlice.actions;

export const selectAbsences = (state: RootState) => state.absences.items;
export const selectAbsencesTotalCount = (state: RootState) => state.absences.totalCount;
export const selectAbsencesRange = (state: RootState) => state.absences.rangeItems;
export const selectAbsencesForDay = (date: string) => (state: RootState) =>
  state.absences.dayItems[date];
export const selectAbsencesStatus = (state: RootState) => state.absences.status;
export const selectAbsencesError = (state: RootState) => state.absences.lastError;

export default absencesSlice.reducer;
